import React from 'react';

import { FullPageModal, Modal, StackingLayout } from '@nutanix-ui/prism-reactjs';
import type { FullPageModalProps } from '@nutanix-ui/prism-reactjs/lib/components/v2/Modal/FullPageModal';
import type { ModalProps } from '@nutanix-ui/prism-reactjs/lib/components/v2/Modal/Modal';

import Button, { ButtonTypes } from '../Button/Button';
import FlexLayout from '../FlexLayout/FlexLayout';

export type DialogProps = Omit<ModalProps & FullPageModalProps, 'size' | 'onClose' | 'selected'> & {
  isOpen?: boolean;
  isCloseDisabled?: boolean;
  actions?: JSX.Element | null;
  dialogBodyClassName?: string;
  hideFooter?: boolean;
  size?: DialogSize;
  cancelButtonText?: string;
  cancelButtonLabel?: string;
  hideClose?: boolean;
  restoreFocus?: boolean;
  onClose?: (payload?: unknown) => void;
};

export enum DialogSize {
  DEFAULT = 'default',
  SMALL = 'small',
  LARGE = 'large',
  MEDIUM = 'medium',
  EXTRA_LARGE = 'extra-large',
  FULL_WIDTH = 'full-width',
  FULL_SCREEN = 'full-screen',
}

export const dialogSizeToWidth = {
  [DialogSize.DEFAULT]: '500px',
  [DialogSize.SMALL]: '300px',
  [DialogSize.MEDIUM]: '600px',
  [DialogSize.LARGE]: '800px',
  [DialogSize.EXTRA_LARGE]: '1000px',
  [DialogSize.FULL_WIDTH]: '100%',
};

export default function Dialog({
  isOpen = false,
  children,
  hideFooter,
  isCloseDisabled,
  hideClose,
  cancelButtonText,
  cancelButtonLabel,
  actions,
  dialogBodyClassName,
  size = DialogSize.DEFAULT,
  restoreFocus = true,
  onClose,
  ...modalProps
}: React.PropsWithChildren<DialogProps>) {
  if (size === DialogSize.FULL_SCREEN) {
    return (
      <FullPageModal
        data-test="full-page-modal"
        {...modalProps}
        visible={isOpen}
        onClose={onClose}
        closeOnEscape={true}
        restoreFocus={restoreFocus}
        closeButtonProps={{ disabled: isCloseDisabled }}
      >
        <StackingLayout className={dialogBodyClassName}>{children}</StackingLayout>
      </FullPageModal>
    );
  }
  return (
    <Modal
      data-test="modal"
      {...modalProps}
      visible={isOpen}
      onClose={onClose}
      closeOnEscape={true}
      restoreFocus={restoreFocus}
      closeButtonProps={{ disabled: isCloseDisabled }}
      width={dialogSizeToWidth[size]}
      footer={
        !hideFooter && (
          <FlexLayout justifyContent="flex-end">
            {!hideClose && (
              <Button
                onClick={onClose}
                disabled={isCloseDisabled}
                type={ButtonTypes.SECONDARY}
                label={cancelButtonLabel ?? 'Cancel'}
              >
                {cancelButtonText ?? 'Cancel'}
              </Button>
            )}
            {actions}
          </FlexLayout>
        )
      }
    >
      <StackingLayout padding="20px" className={dialogBodyClassName}>
        {children}
      </StackingLayout>
    </Modal>
  );
}
